// src/App.js
import './App.css';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { db } from './firebase'; // Ensure this path is correct and db is exported
import { collection, query, getDocs, where } from 'firebase/firestore';

// Components
import ScrollToTop from './components/ScrollToTop';
import Header from './components/Header';
import Footer from './components/Footer';
import ProductDetail from "./components/ProductDetail";
import Landing from './components/Landing';
import Products from './components/Products';
import Admin from './components/Admin';
import CategoryPage from './components/CategoryPage';
// Notifications
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function App() {
  const [products, setProducts] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Also check if the code was validated as domestic previously
    // For simplicity now, we'll just rely on isAuthenticated.
    // A more robust solution might store a 'isDomesticValidated' flag too.
    return localStorage.getItem('isAuthenticated') === 'true';
  });
  const [loggedInCustomerCode, setLoggedInCustomerCode] = useState(() => {
    return localStorage.getItem('customerCode') || null;
  });
  const [customerDiscount, setCustomerDiscount] = useState(() => {
    const storedDiscount = localStorage.getItem('customerDiscount');
    return storedDiscount ? parseFloat(storedDiscount) : 0;
  });

  const [customerCodeInput, setCustomerCodeInput] = useState('');
  const [isCheckingCode, setIsCheckingCode] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const q = query(collection(db, 'products')); // Consider adding orderBy if needed
        const querySnapshot = await getDocs(q);
        const productsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProducts(productsData);
      } catch (error) {
        console.error('Error fetching products:', error);
        toast.error("Failed to load products. Please try refreshing.");
      }
    };

    if (isAuthenticated) {
        fetchProducts();
    }
  }, [isAuthenticated]);

  // Handle customer code submission for login
  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    if (!customerCodeInput || customerCodeInput.length !== 4 || !/^\d+$/.test(customerCodeInput)) {
      toast.warn('Please enter a valid 4-digit customer code.');
      return;
    }
    setIsCheckingCode(true);

    try {
      const codesRef = collection(db, 'codes');
      // Query for the code entered by the user
      const q = query(codesRef, where('code', '==', customerCodeInput.trim())); // Ensure to trim
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const customerDoc = querySnapshot.docs[0];
        const customerData = customerDoc.data();

        // --- *** THE CORE CHANGE IS HERE *** ---
        // Check if the code is marked as domestic
        if (customerData.isDomestic === true) {
          // Code is domestic, proceed with login
          const fetchedDiscount = customerData.discount;
          const validDiscount = (typeof fetchedDiscount === 'number' && fetchedDiscount >= 0) ? fetchedDiscount : 0;

          setIsAuthenticated(true);
          setLoggedInCustomerCode(customerCodeInput.trim());
          setCustomerDiscount(validDiscount);

          localStorage.setItem('isAuthenticated', 'true');
          localStorage.setItem('customerCode', customerCodeInput.trim());
          localStorage.setItem('customerDiscount', validDiscount.toString());

          toast.success(`Login successful! `);
          // Products will be fetched by the useEffect hook listening to isAuthenticated
        } else {
          // Code exists but is NOT domestic (or isDomestic is false/undefined)
          toast.error('Error checking code. Please try again.');
          // Do NOT set isAuthenticated to true
        }
        // --- *** END OF CORE CHANGE *** ---

      } else {
        // Code does not exist in the database
        toast.error('Incorrect Customer Code.');
      }
    } catch (error) {
      console.error('Error verifying customer code:', error);
      toast.error('Error checking code. Please try again.');
    } finally {
      setIsCheckingCode(false);
    }
  };

  // --- Login Screen ---
  if (!isAuthenticated) {
    return (
        <div className="App login-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f0f2f5', padding: '20px' }}>
          <ToastContainer position="top-center" autoClose={3000} hideProgressBar newestOnTop />
           <form onSubmit={handleCodeSubmit} style={{ background: 'white', padding: '40px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', textAlign: 'center', maxWidth: '400px', width: '100%' }}>
              <h1 style={{ color: '#333', marginBottom: '10px', fontSize: '1.8em' }}>Welcome to Amcouch Wholesale</h1>
              <h2 style={{ color: '#555', marginBottom: '25px', fontSize: '1.1em', fontWeight: 'normal' }}>Please enter your Customer Code</h2>
              <input
                className='input login-input'
                type="password" // Keep as password to obscure input, or text if preferred
                maxLength="4"
                inputMode="numeric" // Helps mobile users get numeric keyboard
                pattern="\d{4}" // Basic HTML5 validation for 4 digits
                value={customerCodeInput}
                onChange={(e) => setCustomerCodeInput(e.target.value)}
                style={{ width: '100%', padding: '12px', marginBottom: '20px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '1.2em', textAlign: 'center', letterSpacing: '0.3em' }}
                placeholder='_ _ _ _'
                disabled={isCheckingCode}
                autoFocus
              />
              <button type="submit" className="button submit-button" style={{ width: '100%', padding: '12px 0', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', fontSize: '1.1em', cursor: 'pointer', transition: 'background-color 0.2s' }} disabled={isCheckingCode}>
                {isCheckingCode ? 'Verifying...' : 'Enter Site'}
              </button>
              <p style={{ marginTop: '25px', fontSize: '0.9em', color: '#666' }}>
                Don't have a code? Please message us on WhatsApp: <br/>
                <a href="https://wa.me/919811192812" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', textDecoration: 'none' }}>
                  +91 98111 92812
                </a>
             </p>
           </form>
        </div>
    );
  }

  // --- Authenticated App View ---
  return (
    <div className="App">
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <Router>
        <ScrollToTop />
        <Header products={products} loggedInCustomerCode={loggedInCustomerCode} />
        <main className="main-content">
            <Routes>
            <Route
                path="/"
                element={<Landing customerDiscount={customerDiscount} loggedInCustomerCode={loggedInCustomerCode} />}
            />
            <Route
                path="/products/:id"
                element={<ProductDetail products={products} loggedInCustomerCode={loggedInCustomerCode} customerDiscount={customerDiscount} />}
            />
            <Route
                path="/products"
                element={<Products loggedInCustomerCode={loggedInCustomerCode} customerDiscount={customerDiscount} />}
            />
            <Route
                path="/category/:categoryName"
                element={<CategoryPage customerDiscount={customerDiscount} loggedInCustomerCode={loggedInCustomerCode} />}
            />
            <Route path="/admin" element={<Admin />} /> {/* Assuming Admin is independent of this login */}
            {/* Add a catch-all or Not Found route if desired */}
            {/* <Route path="*" element={<NotFoundComponent />} /> */}
            </Routes>
        </main>
        <Footer/>
      </Router>
    </div>
  );
}

export default App;